import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Share, MonitorDown, Bell, X } from "lucide-react";
import { toast } from "sonner";
import { enablePushNotifications } from "@/lib/push";

const EASE = [0.22, 1, 0.36, 1];

// À chaque connexion : propose d'installer l'icône JULIE sur l'appareil (téléphone,
// tablette, ordinateur) ET d'autoriser les notifications du site — tant que ce n'est pas fait.
export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(() => window.__ravInstall || null);
  const [notifOk, setNotifOk] = useState(
    () => typeof Notification !== "undefined" && Notification.permission === "granted"
  );
  const location = useLocation();

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
  const canAskNotif = typeof Notification !== "undefined" && Notification.permission === "default";

  useEffect(() => {
    const onInstallable = () => setDeferred(window.__ravInstall);
    window.addEventListener("rav-installable", onInstallable);
    return () => window.removeEventListener("rav-installable", onInstallable);
  }, []);

  useEffect(() => {
    // Proposée à TOUTE personne qui ouvre le site, sur tout appareil, à chaque session.
    // Seules exceptions : fermée pendant cette visite, ou pages d'entrée avec formulaire
    // (Gate, Bienvenue, Connexion) — la bannière ne doit JAMAIS recouvrir un bouton
    // d'action (elle forçait un double tap sur « Valider »).
    // APP DÉJÀ INSTALLÉE (ex. iPhone) : on ne re-propose pas l'installation, mais on
    // propose les NOTIFICATIONS — les push iPhone ne sont possibles que dans l'app
    // installée, il faut donc que la demande soit possible dans ce mode aussi.
    if (sessionStorage.getItem("rav-install-shown")) return;
    if (["/", "/bienvenue", "/connexion"].includes(location.pathname)) return;
    if (isStandalone && (notifOk || !canAskNotif)) return;
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => {});
      window.__ravInstall = null;
      setDeferred(null);
    }
  };

  const askNotifications = async () => {
    const res = await enablePushNotifications();
    if (res === "granted") {
      setNotifOk(true);
      toast("Notifications activées — vous serez prévenue en douceur chaque matin.");
    } else if (res === "no-sw-push") {
      setNotifOk(true); // permission donnée : les messages restent visibles dans l'app
      toast("Notifications activées dans l'app. Sur iPhone : installez l'app sur l'écran d'accueil pour les recevoir même app fermée.");
    } else if (res === "denied") {
      toast("Les notifications restent désactivées — modifiable dans les réglages de votre navigateur.");
    } else {
      toast("Les notifications ne sont pas disponibles sur cet appareil.");
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("rav-install-shown", "1");
    setVisible(false);
  };

  // Quand la proposition est visible, la page garde de la place en bas : rien d'important
  // (comme « Créer mon INSTANT ») ne reste coincé sous la fenêtre.
  useEffect(() => {
    document.body.classList.toggle("rav-install-open", visible);
    return () => document.body.classList.remove("rav-install-open");
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="rav-install"
          data-testid="install-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div className="rav-install-head">
            <img src="/icons/icon-192.png" alt="" aria-hidden="true" className="rav-install-icon" />
            <div className="rav-install-text">
              <p className="rav-install-title">
                {isStandalone ? "Votre douceur du matin" : "Gardez Respire Aime Vibre dans votre poche"}
              </p>
              {isStandalone ? (
                <p className="rav-install-sub">
                  Activez les notifications : chaque matin, un petit message vous invite à prendre un Instant pour vous.
                </p>
              ) : isIOS && !deferred ? (
                <p className="rav-install-sub">
                  Touchez <Share size={13} strokeWidth={1.8} style={{ verticalAlign: "-2px" }} /> « Partager » puis
                  « Sur l'écran d'accueil » — sur votre téléphone ou votre tablette.
                </p>
              ) : deferred ? (
                <p className="rav-install-sub">Installez l'icône sur votre téléphone, tablette ou ordinateur — un toucher suffit.</p>
              ) : (
                <p className="rav-install-sub">
                  Depuis le menu de votre navigateur, choisissez « Installer l'application » ou « Ajouter à l'écran d'accueil ».
                </p>
              )}
            </div>
            <button type="button" className="rav-install-later" data-testid="install-later" onClick={dismiss} aria-label="Plus tard">
              <X size={16} strokeWidth={1.6} />
            </button>
          </div>
          {(deferred || (!notifOk && canAskNotif)) && (
            <div className="rav-install-actions">
              {deferred && (
                <button type="button" className="rav-primary-button rav-install-btn" data-testid="install-confirm" onClick={install}>
                  <MonitorDown size={15} strokeWidth={1.6} /> Installer l'application
                </button>
              )}
              {!notifOk && canAskNotif && (
                <button type="button" className="rav-secondary-button rav-install-btn rav-install-btn-soft" data-testid="install-notifications" onClick={askNotifications}>
                  <Bell size={15} strokeWidth={1.6} /> Autoriser les notifications
                </button>
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
